import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, filter, fromEvent, map, merge, of, share, switchMap, timer } from 'rxjs';

export interface CampaignRefreshOption {
  ms: number;
  labelKey: string;
}

export const CAMPAIGN_REFRESH_OPTIONS: Array<CampaignRefreshOption> = [
  { ms: 5_000, labelKey: 'campaign.refreshInterval.every5s' },
  { ms: 15_000, labelKey: 'campaign.refreshInterval.every15s' },
  { ms: 30_000, labelKey: 'campaign.refreshInterval.every30s' },
  { ms: 60_000, labelKey: 'campaign.refreshInterval.every1min' },
];

const DEFAULT_INTERVAL_MS = 60_000;
const STORAGE_KEY = 'campaign:refreshIntervalMs';

/**
 * The SINGLE polling-cadence preference of the campaign screens (DEV-2034 —
 * replaced the WebSocket push): the dispatch list and the dispatch panel read
 * the same value, persisted in localStorage. Defaults to 1 minute — whoever
 * watches a dispatch closely shortens it; whoever only schedules pays nothing
 * beyond that.
 */
@Injectable({ providedIn: 'root' })
export class CampaignRefreshPreferenceService {

  private readonly intervalSubject = new BehaviorSubject<number>(this.restore());

  private readonly becameVisible$ = fromEvent(document, 'visibilitychange').pipe(
    filter(() => document.visibilityState === 'visible'),
  );

  /**
   * The polling tick every campaign screen subscribes to. One pipeline owns
   * the whole cadence semantics, so the list and the panel can never drift:
   *
   * - fires every `intervalMs` while the tab is visible; ticks while hidden
   *   are dropped;
   * - fires immediately when the tab becomes visible again, and RESTARTS the
   *   cadence from that moment — a return near a tick boundary never causes a
   *   back-to-back double refresh;
   * - fires nothing on subscribe: the screen's initial load covers that.
   */
  readonly ticks$: Observable<void> = this.intervalSubject.pipe(
    switchMap((ms) =>
      merge(of('start' as const), this.becameVisible$).pipe(
        switchMap((origin) => (origin === 'start' ? timer(ms, ms) : timer(0, ms))),
      ),
    ),
    filter(() => document.visibilityState === 'visible'),
    map(() => undefined),
    share(),
  );

  get intervalMs(): number {
    return this.intervalSubject.value;
  }

  setIntervalMs(ms: number): void {
    if (!CAMPAIGN_REFRESH_OPTIONS.some((option) => option.ms === ms)) {
      return;
    }
    // Re-picking the active option must not re-emit: the pipeline above
    // restarts its timer on every emission, which would push the next refresh
    // a full period away for both screens.
    if (ms === this.intervalSubject.value) {
      return;
    }
    this.intervalSubject.next(ms);
    try {
      localStorage.setItem(STORAGE_KEY, String(ms));
    } catch {
      // The preference is a convenience: without storage it lasts the session.
    }
  }

  private restore(): number {
    try {
      const stored = Number(localStorage.getItem(STORAGE_KEY));
      if (CAMPAIGN_REFRESH_OPTIONS.some((option) => option.ms === stored)) {
        return stored;
      }
    } catch {
      // Without storage (private tab etc.) the default covers it.
    }
    return DEFAULT_INTERVAL_MS;
  }

}
